/**
 * Service to handle image-related API calls
 */

const API_URL = process.env.REACT_APP_API_URL;

/**
 * Generate a photo for a character
 * @param {String} token - Firebase Auth ID token
 * @param {Object} params - Generation parameters
 * @returns {Promise<object>} - Result containing generated images
 */
export const generateCharacterPhoto = async (token, params) => {
  const response = await fetch(`${API_URL}/images/generate`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify(params),
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Failed to generate photo');
  }

  return data;
};

/**
 * Delete a generated photo by its Firestore document ID.
 * @param {String} token - Firebase Auth ID token
 * @param {String} photoId - Firestore document ID of the photo to delete
 * @returns {Promise<object>} - Result of deletion
 */
export const deletePhoto = async (token, photoId) => {
  const response = await fetch(`${API_URL}/images/delete`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({ photoId }),
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Failed to delete photo');
  }

  return data;
};

/**
 * Fetch paginated gallery of generated photos for a character.
 * @param {String} token
 * @param {String} characterId
 * @param {String|null} cursor - ISO date string of last item's createdAt (for pagination)
 * @param {Number} limit
 */
export const getCharacterGallery = async (token, characterId, cursor = null, limit = 20) => {
  const payload = { characterId, limit };
  if (cursor) payload.cursor = cursor;

  const response = await fetch(`${API_URL}/images/gallery`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
    body: JSON.stringify(payload),
  });

  const data = await response.json();
  if (!response.ok || !data.success) throw new Error(data.error || 'Failed to fetch gallery');
  return data.data; // { images: [], nextCursor: string | null }
};

/**
 * Fetch images for a character with pagination.
 * @param {String} token - Firebase Auth ID token
 * @param {String} characterId - ID of the character
 * @param {Object} params - Pagination parameters (e.g., page, limit)
 * @returns {Promise<object>} - Result containing images
 */
export const getCharacterImages = async (token, characterId, params) => {
  const response = await fetch(`${API_URL}/images/gallery`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({ characterId, ...params }),
  });

  const data = await response.json();
  console.log('[getCharacterImages] Response:', data);

  if (!response.ok) {
    throw new Error(data.error || 'Failed to fetch character images');
  }

  return data;
};

/**
 * Delete a character image by its ID.
 * @param {String} token - Firebase Auth ID token
 * @param {String} imageId - ID of the image to delete
 * @returns {Promise<void>} - Result of deletion
 */
export const deleteCharacterImage = async (token, imageId) => {
  return deletePhoto(token, imageId);
};

